import { Link } from 'react-router-dom';
import { Zap, Shield, Clock, Gift, ArrowRight, Star, Users, Trophy, Copy, Check, Lock, CreditCard, Bitcoin, CheckCircle, HelpCircle, ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import BonusCalculator from '../components/BonusCalculator';
import Footer from '../components/Footer';

const Home = () => {
  const [copied, setCopied] = useState(false);
  const promoCode = 'CS2026';

  useEffect(() => {
    // WebSite Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "CSGO Luck Promo Code",
      "url": "https://csgoluckpromocode.com",
      "description": "Get the best CSGOLuck promo code CS2026 for a 100% deposit bonus up to $70. Exclusive welcome bonus for new players in 2026.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://csgoluckpromocode.com/?s={search_term_string}",
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "name": "CSGO Luck Promo Code",
        "url": "https://csgoluckpromocode.com"
      }
    };

    const wsScript = document.createElement('script');
    wsScript.type = 'application/ld+json';
    wsScript.text = JSON.stringify(websiteSchema);
    wsScript.id = 'website-schema';
    document.head.appendChild(wsScript);

    // FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the best CSGOLuck promo code?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The best CSGOLuck promo code is CS2026. With this exclusive code, new players will receive a 100% deposit bonus up to 100 coins ($70)."
          }
        },
        {
          "@type": "Question",
          "name": "What is the minimum deposit to get the CSGOLuck bonus?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The minimum deposit to trigger the bonus is $30 (42.86 coins). All deposits less than the mentioned amount will not trigger the welcome bonus offer."
          }
        },
        {
          "@type": "Question",
          "name": "How much bonus can I get?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The maximum amount of bonus money you can get from the CSGOLuck referral code is 100 coins ($70). Even if you deposit more than 100 coins, you will still receive the same amount."
          }
        },
        {
          "@type": "Question",
          "name": "Are there wagering requirements for the CSGOLuck affiliate code?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, CSGOLuck put in place 40x wagering requirements. Only after the wagering requirements are met will a bonus withdrawal be possible."
          }
        },
        {
          "@type": "Question",
          "name": "Is CS2026 a legit CSGO Luck promo code?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! This is an official code received through the collaboration with CSGOLuck representatives. The code has a proven track record and will deliver on a matched deposit bonus."
          }
        },
        {
          "@type": "Question",
          "name": "Is there a maximum bonus limit for CSGOLuck welcome bonus?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Based on the bonus terms and conditions, the maximum bonus amount that can be cashed out from the website is 1,500 coins ($1,050)."
          }
        }
      ]
    };

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify(faqSchema);
    faqScript.id = 'faq-schema';
    document.head.appendChild(faqScript);

    return () => {
      const ws = document.getElementById('website-schema');
      if (ws) ws.remove();
      const faq = document.getElementById('faq-schema');
      if (faq) faq.remove();
    };
  }, []);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(promoCode);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = promoCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success('Promo code copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const features = [
    {
      icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: '100% Deposit Bonus',
      description: 'Double your first deposit up to 100 coins ($70)',
    },
    {
      icon: <Shield className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Provably Fair Games',
      description: 'All games on the site are verified, transparent, and fair',
    },
    {
      icon: <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Transparent Requirements',
      description: 'Bonus terms and conditions are transparent and easy to fulfill',
    },
    {
      icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Use Bonus Money Anywhere',
      description: 'Play Originals, bet on sports, open cases & more',
    },
  ];

  const stats = [
    { value: '50K+', label: 'Active Players', icon: <Users className="w-4 h-4 sm:w-5 sm:h-5" /> },
    { value: '$2M+', label: 'Paid Out', icon: <Trophy className="w-4 h-4 sm:w-5 sm:h-5" /> },
    { value: '4.8', label: 'User Rating', icon: <Star className="w-4 h-4 sm:w-5 sm:h-5" /> },
  ];

  return (
    <div className="min-h-screen bg-csgo-bg">
      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24 md:pt-32 pb-12 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] md:w-[800px] h-[400px] sm:h-[600px] md:h-[800px] gradient-radial" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left" data-testid="hero-content">
              <h1 className="font-orbitron font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-3 sm:mb-4">
                CSGO Luck <span className="text-csgo-primary neon-text">Promo Code</span> 2026
              </h1>
              
              <p className="font-orbitron font-bold text-xl sm:text-2xl md:text-3xl text-csgo-secondary mb-4 sm:mb-6">
                Get <span className="text-csgo-primary">100%</span> Bonus Up to $70
              </p>
              
              <p className="font-rajdhani text-base sm:text-lg md:text-xl text-csgo-textMuted leading-relaxed mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0">
                Use promo code <span className="text-csgo-primary font-bold">CS2026</span> and 
                double your first deposit on CSGO Luck - the premier CS2 skin gambling site.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <a
                  href="https://csgoluck.com/r/CS2026"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-claim-btn"
                  className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-csgo-primary text-black font-orbitron font-bold text-sm sm:text-base uppercase tracking-wider rounded-xl hover:bg-csgo-primary/90 transition-all duration-200 neon-glow hover:scale-105"
                >
                  Claim Bonus Now
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <Link
                  to="/how-to-claim"
                  data-testid="hero-learn-btn"
                  className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-csgo-primary/50 text-csgo-primary font-orbitron font-bold text-sm sm:text-base uppercase tracking-wider rounded-xl hover:bg-csgo-primary/10 hover:border-csgo-primary transition-all duration-200"
                >
                  How It Works
                </Link>
              </div>
            </div>

            {/* Right Content - Calculator */}
            <div className="lg:pl-8 mt-4 lg:mt-0">
              <BonusCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-12 border-y border-csgo-border bg-csgo-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-3 sm:gap-8" data-testid="stats-section">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-csgo-primary/10 rounded-lg mb-1.5 sm:mb-3 text-csgo-primary">
                  {stat.icon}
                </div>
                <p className="font-orbitron font-black text-lg sm:text-2xl md:text-3xl text-white">{stat.value}</p>
                <p className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-orbitron font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-3 sm:mb-4">
              Why Use <span className="text-csgo-primary">CSGOLuck</span> Bonus Code?
            </h2>
            <p className="font-rajdhani text-base sm:text-lg text-csgo-textMuted max-w-2xl mx-auto">
              Join thousands of players who trust CSGO Luck for fair gaming and instant rewards
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-csgo-surface/60 backdrop-blur-sm border border-csgo-border rounded-xl p-5 sm:p-6 card-hover group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-csgo-primary/10 rounded-lg flex items-center justify-center mb-3 sm:mb-4 text-csgo-primary group-hover:bg-csgo-primary group-hover:text-black transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-orbitron font-bold text-base sm:text-lg text-white mb-2">{feature.title}</h3>
                <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Combined CTA + Bonus Details Section */}
      <section className="py-12 sm:py-16 md:py-24 relative overflow-hidden" data-testid="bonus-details-section">
        <div className="absolute inset-0 bg-gradient-to-r from-csgo-primary/5 to-csgo-secondary/5" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="bg-csgo-surface/80 backdrop-blur-md border border-csgo-border rounded-2xl p-6 sm:p-8 md:p-12">
            
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="font-orbitron font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-3">
                Claim Your <span className="text-csgo-primary neon-text">$70 Bonus</span> on CSGOLuck
              </h2>
              <p className="font-rajdhani text-base sm:text-lg text-csgo-textMuted">
                Use the code below and claim 100% match deposit bonus
              </p>
            </div>

            {/* Promo Code with Copy */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              <button
                onClick={handleCopy}
                data-testid="home-copy-code-btn"
                className="group flex items-center gap-3 sm:gap-4 bg-csgo-bg border-2 border-csgo-primary/50 rounded-xl px-6 sm:px-8 py-3 sm:py-4 hover:border-csgo-primary transition-all duration-300 cursor-pointer w-full sm:w-auto justify-center"
              >
                <span className="font-orbitron font-black text-2xl sm:text-3xl text-csgo-primary tracking-widest neon-text">
                  {promoCode}
                </span>
                <div className={`p-2 rounded-lg transition-all duration-300 ${copied ? 'bg-csgo-primary text-black' : 'bg-csgo-surface text-csgo-primary group-hover:bg-csgo-primary/20'}`}>
                  {copied ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5" />}
                </div>
              </button>
              <span className="font-rajdhani text-sm text-csgo-textMuted">
                {copied ? 'Copied!' : 'Click to copy'}
              </span>
            </div>

            <div className="text-center mb-8 sm:mb-10">
              <a
                href="https://csgoluck.com/r/CS2026"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-claim-btn"
                className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-csgo-primary text-black font-orbitron font-bold text-base sm:text-lg uppercase tracking-wider rounded-xl hover:bg-csgo-primary/90 transition-all duration-200 neon-glow hover:scale-105 w-full sm:w-auto"
              >
                Claim Bonus Now
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </div>

            <div className="border-t border-csgo-border my-6 sm:my-8"></div>

            <div>
              <h3 className="font-orbitron font-bold text-base sm:text-lg text-white mb-4 sm:mb-6 flex items-center gap-3">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-csgo-primary" />
                Bonus Details
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                {[
                  { label: 'Bonus Type', value: '100% Match', color: 'text-white' },
                  { label: 'Max Bonus', value: '100 coins ($70)', color: 'text-csgo-primary' },
                  { label: 'Min Deposit', value: '$30', color: 'text-csgo-secondary' },
                  { label: 'Wagering', value: '40x', color: 'text-white' },
                  { label: 'Max Payout', value: '1,500 coins', color: 'text-white' },
                  { label: 'Expiry', value: '31.12.2026', color: 'text-white' },
                ].map((item, i) => (
                  <div key={i} className="bg-csgo-bg/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="font-rajdhani text-xs text-csgo-textMuted uppercase tracking-wider mb-1">{item.label}</p>
                    <p className={`font-orbitron font-bold text-sm sm:text-base ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-xs sm:text-sm text-csgo-textMuted mt-4 sm:mt-6 font-rajdhani">
            Valid for new players only. Terms and conditions apply.
          </p>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-12 sm:py-16 md:py-20 border-t border-csgo-border" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl md:text-3xl text-white mb-2">
              Trusted & <span className="text-csgo-primary">Secure</span>
            </h2>
            <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted">
              CSGO Luck is a verified and secure platform
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
            {[
              { label: 'Provably Fair', icon: <Shield className="w-4 h-4 sm:w-5 sm:h-5" /> },
              { label: 'SSL Secured', icon: <Lock className="w-4 h-4 sm:w-5 sm:h-5" /> },
              { label: 'Instant Payouts', icon: <Clock className="w-4 h-4 sm:w-5 sm:h-5" /> },
              { label: 'Verified', icon: <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> },
            ].map((badge, index) => (
              <div 
                key={index}
                className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-csgo-surface/60 rounded-xl border border-csgo-border hover:border-csgo-primary/30 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-csgo-primary/10 rounded-lg flex items-center justify-center text-csgo-primary">
                  {badge.icon}
                </div>
                <span className="font-rajdhani font-semibold text-white text-xs sm:text-sm text-center">{badge.label}</span>
              </div>
            ))}
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <p className="font-rajdhani text-csgo-textMuted text-xs sm:text-sm mb-4 sm:mb-6 uppercase tracking-wider">
              Accepted Payment Methods
            </p>
            
            <div className="mb-4 sm:mb-6">
              <p className="font-rajdhani text-xs text-csgo-textMuted uppercase tracking-wider mb-2 sm:mb-3">Other Methods</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { name: 'CS2 Skins', label: 'CS2' },
                  { name: 'Gift Cards', label: 'GIFT' },
                ].map((method, index) => (
                  <div key={index} className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-csgo-surface rounded-lg border border-csgo-border text-csgo-textMuted hover:text-white hover:border-csgo-primary/30 transition-colors">
                    <span className="font-orbitron font-bold text-xs text-csgo-primary">{method.label}</span>
                    <span className="font-rajdhani text-xs sm:text-sm">{method.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <p className="font-rajdhani text-xs text-csgo-textMuted uppercase tracking-wider mb-2 sm:mb-3">Real Money</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { name: 'Visa', label: 'VISA' },
                  { name: 'Mastercard', label: 'MC' },
                ].map((method, index) => (
                  <div key={index} className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-csgo-surface rounded-lg border border-csgo-border text-csgo-textMuted hover:text-white hover:border-csgo-primary/30 transition-colors">
                    <span className="font-orbitron font-bold text-xs text-csgo-secondary">{method.label}</span>
                    <span className="font-rajdhani text-xs sm:text-sm">{method.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="font-rajdhani text-xs text-csgo-textMuted uppercase tracking-wider mb-2 sm:mb-3">Cryptocurrency</p>
              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                {[
                  'BTC', 'ETH', 'LTC', 'USDT', 'USDC', 'BNB', 'SOL', 'ADA', 
                  'AVAX', 'BCH', 'DOGE', 'LINK', 'POL', 'TRX', 'UNI', 'XRP'
                ].map((crypto, index) => (
                  <div key={index} className="px-2 sm:px-3 py-1 sm:py-2 bg-csgo-surface rounded-lg border border-csgo-border text-csgo-textMuted hover:text-white hover:border-csgo-primary/30 transition-colors">
                    <span className="font-orbitron font-bold text-xs">{crypto}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20 border-t border-csgo-border" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl md:text-3xl text-white mb-2">
              Frequently Asked <span className="text-csgo-primary">Questions</span>
            </h2>
            <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted">
              Everything you need to know about the CSGOLuck codes
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {[
              {
                question: 'What is the best CSGOLuck promo code?',
                answer: 'The best CSGOLuck promo code is CS2026. With this exclusive code, new players will receive a 100% deposit bonus up to 100 coins ($70).'
              },
              {
                question: 'What is the minimum deposit to get the CSGOLuck bonus?',
                answer: 'The minimum deposit to trigger the bonus is $30 (42.86 coins). All deposits less than the mentioned amount will not trigger the welcome bonus offer.'
              },
              {
                question: 'How much bonus can I get?',
                answer: 'The maximum amount of bonus money you can get from the CSGOLuck referral code is 100 coins ($70). Even if you deposit more than 100 coins, you will still receive the same amount.'
              },
              {
                question: 'Are there wagering requirements for the CSGOLuck affiliate code?',
                answer: 'Yes, CSGOLuck put in place 40x wagering requirements. Only after the wagering requirements are met will a bonus withdrawal be possible.'
              },
              {
                question: 'Is CS2026 a legit CSGO Luck promo code?',
                answer: 'Yes! This is an official code received through the collaboration with CSGOLuck representatives. The code has a proven track record and will deliver on a matched deposit bonus.'
              },
              {
                question: 'Is there a maximum bonus limit for CSGOLuck welcome bonus?',
                answer: 'Based on the bonus terms and conditions, the maximum bonus amount that can be cashed out from the website is 1,500 coins ($1,050).'
              },
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-csgo-surface/60 border border-csgo-border rounded-xl overflow-hidden"
              >
                <div className="p-4 sm:p-5">
                  <h3 className="font-orbitron font-bold text-sm sm:text-base text-white flex items-start gap-2 sm:gap-3">
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-csgo-primary flex-shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted mt-2 sm:mt-3 pl-6 sm:pl-8">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
